import React, { useState } from 'react';
import { Chess } from 'chess.js';

const MobilePGNUploadModal = ({ isOpen, onClose, onPGNSubmit }) => {
  const [pgnInput, setPgnInput] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Comprehensive PGN processor that creates moves identical to manual play
   * Adapted from FENBuilderModal.js for mobile use
   */
  const processComprehensivePGN = (pgnInput) => {
    try {
      // Create a fresh chess instance for processing
      const chess = new Chess();
      
      // Preprocess PGN string
      let processedPgn = pgnInput.replace(/\r?\n/g, ' ').trim();
      processedPgn = processedPgn.replace(/\s+/g, ' ');
      processedPgn = processedPgn.replace(/(\d+)\.([a-zA-Z0-9])/g, '$1. $2');

      // Try to load and validate PGN
      let isValidPGN = false;
      try {
        // Try the newer API first (loadPgn), then fall back to older API (load_pgn)
        if (typeof chess.loadPgn === 'function') {
          isValidPGN = chess.loadPgn(processedPgn, { sloppy: true });
        } else if (typeof chess.load_pgn === 'function') {
          isValidPGN = chess.load_pgn(processedPgn, { sloppy: true });
        } else {
          console.error('[MobilePGNUploadModal] No PGN loading method found on chess instance');
          throw new Error('PGN loading not supported by chess.js version');
        }
      } catch (e) {
        console.warn('[MobilePGNUploadModal] PGN validation failed:', e.message);
      }

      if (!isValidPGN) {
        console.error('[MobilePGNUploadModal] processComprehensivePGN - PGN INVALID');
        
        // Try manual PGN parsing as fallback
        try {
          const chess = new Chess();
          const comprehensiveMoves = [];
          
          // Extract moves from PGN manually
          // Remove headers and comments
          let moveText = processedPgn.replace(/\[.*?\]/g, '').replace(/\{.*?\}/g, '').trim();
          
          // Extract individual moves using regex
          // This regex matches move notation like e4, Nf3, O-O, Qxh7+, etc.
          const movePattern = /([a-hA-H]?[1-8]?[NBRQK]?[a-hA-H][1-8](?:=[NBRQ])?[+#]?|O-O(?:-O)?[+#]?)/g;
          const moves = [];
          let match;
          
          while ((match = movePattern.exec(moveText)) !== null) {
            const move = match[1];
            // Skip game result indicators
            if (!['1-0', '0-1', '1/2-1/2', '*'].includes(move)) {
              moves.push(move);
            }
          }
          
          if (moves.length === 0) {
            console.error('[MobilePGNUploadModal] No moves extracted from PGN');
            throw new Error('No moves could be extracted from PGN');
          }
          
          // Apply moves one by one
          for (let i = 0; i < moves.length; i++) {
            const moveNotation = moves[i];
            
            // Skip result indicators
            if (['1-0', '0-1', '1/2-1/2', '*'].includes(moveNotation)) {
              break;
            }
            
            const fenBeforeMove = chess.fen();
            
            try {
              const normalizedMove = normalizeChessMove(moveNotation);
              const appliedMove = chess.move(normalizedMove, { sloppy: true });
              
              if (appliedMove) {
                const fenAfterMove = chess.fen();
                
                // Create comprehensive move object directly with all required properties
                const moveObject = {
                  notation: moveNotation,
                  from: appliedMove.from,
                  to: appliedMove.to,
                  startSquare: appliedMove.from,
                  endSquare: appliedMove.to,
                  fen: fenAfterMove,
                  fenBefore: fenBeforeMove,
                  piece: appliedMove.piece,
                  captured: appliedMove.captured || null,
                  promotion: appliedMove.promotion || null,
                  content: '',
                  arrows: [],
                  highlights: []
                };
                
                comprehensiveMoves.push(moveObject);
              } else {
                console.error(`[MobilePGNUploadModal] Manual parsing failed at move: ${moveNotation}`);
                break;
              }
            } catch (e) {
              console.error(`[MobilePGNUploadModal] Manual parsing error at move ${moveNotation}:`, e);
              break;
            }
          }
          
          if (comprehensiveMoves.length > 0) {
            // Analyze game result
            const gameResult = {
              result: "unfinished",
              winner: null,
              reason: "ongoing"
            };

            if (chess.game_over()) {
              if (chess.in_checkmate()) {
                gameResult.result = "checkmate";
                gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
                gameResult.reason = "checkmate";
              } else if (chess.in_draw()) {
                gameResult.result = "draw";
                if (chess.in_stalemate()) {
                  gameResult.reason = "stalemate";
                } else if (chess.insufficient_material()) {
                  gameResult.reason = "insufficient material";
                } else {
                  gameResult.reason = "fifty move rule or repetition";
                }
              }
            }
            
            return {
              success: true,
              moves: comprehensiveMoves,
              gameResult,
              finalFEN: chess.fen()
            };
          } else {
            console.error('[MobilePGNUploadModal] Manual parsing failed - no moves were successfully applied');
          }
          
        } catch (manualError) {
          console.error('[MobilePGNUploadModal] Manual parsing also failed:', manualError);
        }
        
        return {
          success: false,
          error: "Invalid PGN format",
          moves: []
        };
      }

      // Get the move history from chess.js
      const moveHistory = chess.history({ verbose: true });

      // Now rebuild moves step by step to match manual play structure
      const tempChess = new Chess();
      const comprehensiveMoves = [];

      for (let i = 0; i < moveHistory.length; i++) {
        const chessMove = moveHistory[i];
        
        // Get the FEN before this move
        const fenBeforeMove = tempChess.fen();
        
        // Apply the move to our temp chess instance with normalization
        const normalizedMove = normalizeChessMove(chessMove.san);
        const appliedMove = tempChess.move(normalizedMove, { sloppy: true });
        
        if (appliedMove) {
          // Create comprehensive move object directly with all required properties
          const moveObject = {
            notation: chessMove.san,
            from: appliedMove.from,
            to: appliedMove.to,
            startSquare: appliedMove.from,
            endSquare: appliedMove.to,
            fen: tempChess.fen(), // FEN after the move
            fenBefore: fenBeforeMove, // FEN before the move
            piece: appliedMove.piece,
            captured: appliedMove.captured || null,
            promotion: appliedMove.promotion || null,
            content: '',
            arrows: [],
            highlights: []
          };
          
          comprehensiveMoves.push(moveObject);
        } else {
          console.error(`[MobilePGNUploadModal] Failed to apply move: ${chessMove.san}`);
          break;
        }
      }

      // Analyze game result
      const gameResult = {
        result: "unfinished",
        winner: null,
        reason: "ongoing"
      };

      if (chess.game_over()) {
        if (chess.in_checkmate()) {
          gameResult.result = "checkmate";
          gameResult.winner = chess.turn() === 'w' ? 'black' : 'white';
          gameResult.reason = "checkmate";
        } else if (chess.in_draw()) {
          gameResult.result = "draw";
          if (chess.in_stalemate()) {
            gameResult.reason = "stalemate";
          } else if (chess.insufficient_material()) {
            gameResult.reason = "insufficient material";
          } else {
            gameResult.reason = "fifty move rule or repetition";
          }
        }
      }

      return {
        success: true,
        moves: comprehensiveMoves,
        gameResult,
        finalFEN: chess.fen()
      };

    } catch (error) {
      console.error('[MobilePGNUploadModal] processComprehensivePGN - ERROR:', error);
      return {
        success: false,
        error: error.message || "Unknown error during PGN processing",
        moves: []
      };
    }
  };

  /**
   * Normalize chess move notation to standard case:
   * - Files (a-h): lowercase
   * - Pieces (NBRQK): uppercase  
   * - Special moves (O-O, O-O-O): uppercase
   */
  const normalizeChessMove = (move) => {
    if (!move) return move;
    
    // Handle castling - normalize to uppercase O's
    if (move.match(/^[o0]-[o0](-[o0])?[+#]?$/i)) {
      return move.replace(/[o0]/g, 'O');
    }
    
    // For regular moves, apply case rules:
    // 1. Piece letters (N,B,R,Q,K) should be uppercase
    // 2. File letters (a-h) should be lowercase  
    // 3. Promotion pieces should be uppercase
    
    let normalized = move;
    
    // Normalize piece notation at the start (Nf3, Bxc4, etc.)
    normalized = normalized.replace(/^([nbrqk])/i, (match, piece) => piece.toUpperCase());
    
    // Normalize file letters (a-h) to lowercase
    normalized = normalized.replace(/([A-H])([1-8])/g, (match, file, rank) => file.toLowerCase() + rank);
    
    // Normalize promotion notation (=Q, =R, =B, =N)
    normalized = normalized.replace(/=([nbrq])/gi, (match, piece) => '=' + piece.toUpperCase());
    
    return normalized;
  };

  const handlePGNInputChange = (e) => {
    setPgnInput(e.target.value);
    setError(''); // Clear error when user types
  };

  const handleSubmit = async () => {
    if (!pgnInput.trim()) {
      setError('Please enter a PGN');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Use the comprehensive PGN processor first (matching desktop exactly)
      const comprehensiveResult = processComprehensivePGN(pgnInput.trim());

      if (comprehensiveResult.success) {
        // console.log('[MobilePGNUploadModal] Comprehensive PGN processing successful:', {
        //   movesCount: comprehensiveResult.moves.length,
        //   gameResult: comprehensiveResult.gameResult
        // });

        // Create moveList for display compatibility
        const moveList = comprehensiveResult.moves.map((move, index) => ({
          move: move.notation,
          content: move.content || '',
          fen: move.fen,
          moveNumber: Math.floor(index / 2) + 1,
          color: index % 2 === 0 ? 'white' : 'black'
        }));

        // Store comprehensive moves for retrieval (like desktop)
        window._lastComprehensiveResult = comprehensiveResult;

        // Call the parent's submit handler with the processed data
        onPGNSubmit({
          pgnInput: pgnInput.trim(),
          moveList: moveList,
          gameResult: comprehensiveResult.gameResult,
          comprehensiveMoves: comprehensiveResult.moves
        });

        // Reset the modal
        setPgnInput('');
        setError('');
        onClose();
        return;
      } else {
        // Fall back to original processing if comprehensive fails (EXACTLY like desktop)
        // console.log('[MobilePGNUploadModal] Comprehensive processing failed, trying fallback...');
        
        // Clear any stored comprehensive result
        window._lastComprehensiveResult = null;

        const chess = new Chess();
        try {
          // Extract just the moves, ignoring headers and comments (same as desktop)
          const moveText = pgnInput.replace(/\[.*?\]/g, '').replace(/\{.*?\}/g, '');

          // Try to extract moves in a more flexible way (same regex as desktop)
          const movePattern = /(?:\d+\.+\s*)?([a-zA-Z][a-zA-Z0-9\+\#\=\-]+)(?:\s+|$)/g;
          let match;
          let cleanMoves = [];

          while ((match = movePattern.exec(moveText)) !== null) {
            if (match[1]) cleanMoves.push(match[1]);
          }

          // If no moves were found, try a simpler approach - just split by spaces and filter
          if (cleanMoves.length === 0) {
            cleanMoves = moveText.split(/\s+/).filter(move =>
              /^[a-zA-Z][a-zA-Z0-9\+\#\=\-]+$/.test(move) &&
              !move.match(/^\d+\.+$/)
            );
          }

          // Reset and try to apply moves one by one
          chess.reset();
          let allMovesApplied = true;
          let lastSuccessfulFen = chess.fen();
          let appliedMoves = 0;
          let failedMoveIndex = -1;
          let failedMove = null;
          let failureReason = null;
          
          // Create proper moves array with all required properties
          const properMoves = [];

          for (let i = 0; i < cleanMoves.length; i++) {
            const move = cleanMoves[i];
            try {
              const fenBeforeMove = chess.fen();
              const normalizedMove = normalizeChessMove(move);
              const appliedMove = chess.move(normalizedMove, { sloppy: true });

              if (appliedMove) {
                // Create proper move object with all required properties
                const moveObject = {
                  notation: appliedMove.san,
                  from: appliedMove.from,
                  to: appliedMove.to,
                  startSquare: appliedMove.from,
                  endSquare: appliedMove.to,
                  fen: chess.fen(),
                  fenBefore: fenBeforeMove,
                  piece: appliedMove.piece,
                  captured: appliedMove.captured || null,
                  promotion: appliedMove.promotion || null,
                  content: '',
                  arrows: [],
                  highlights: []
                };
                
                properMoves.push(moveObject);
                lastSuccessfulFen = chess.fen();
                appliedMoves++;

              } else {
                allMovesApplied = false;
                failedMoveIndex = i;
                failedMove = move;
                failureReason = "Invalid move";
                console.error(`[MobilePGNUploadModal] Failed! Move ${move} is invalid in this position`);
                break;
              }
            } catch (e) {
              allMovesApplied = false;
              failedMoveIndex = i;
              failedMove = move;
              failureReason = e.message || "Exception during move";
              console.error(`[MobilePGNUploadModal] Exception! Move ${move} caused error:`, e.message || e);
              break;
            }
          }

          if (cleanMoves.length > 0) {
            if (!allMovesApplied) {
              const errorMsg = `Partial success: ${appliedMoves} of ${cleanMoves.length} moves applied. Failed at move: "${failedMove}" (${failureReason})`;
              console.warn(errorMsg);
              setError(errorMsg);
              return;
            } else if (properMoves.length > 0) {
              // SUCCESS: Store the proper moves for comprehensive processing
              // console.log('[MobilePGNUploadModal] Fallback parsing successful, applied', properMoves.length, 'moves');
              
              // Create moveList for display
              const moveList = properMoves.map((move, index) => ({
                move: move.notation,
                content: move.content || '',
                fen: move.fen,
                moveNumber: Math.floor(index / 2) + 1,
                color: index % 2 === 0 ? 'white' : 'black'
              }));
              
              // Store comprehensive moves for retrieval  
              window._lastComprehensiveResult = {
                success: true,
                moves: properMoves,
                gameResult: {
                  result: "unfinished",
                  winner: null,
                  reason: "ongoing"
                },
                finalFEN: lastSuccessfulFen
              };

              // Call the parent's submit handler with the processed data
              onPGNSubmit({
                pgnInput: pgnInput.trim(),
                moveList: moveList,
                gameResult: {
                  result: "unfinished",
                  winner: null,
                  reason: "ongoing"
                },
                comprehensiveMoves: properMoves
              });

              // Reset the modal
              setPgnInput('');
              setError('');
              onClose();
              return;
            } else {
              const errorMsg = "INVALID PGN - NO VALID MOVES FOUND";
              console.error(errorMsg);
              setError(errorMsg);
              return;
            }
          } else {
            const errorMsg = "INVALID PGN - NO VALID MOVES FOUND";
            console.error(errorMsg);
            setError(errorMsg);
            return;
          }
        } catch (e) {
          const errorMsg = `INVALID PGN - PARSING ERROR: ${e.message || e}`;
          console.error("Fallback parsing failed with exception:", e);
          setError(errorMsg);
          return;
        }
      }
    } catch (e) {
      const errorMsg = `INVALID PGN - UNEXPECTED ERROR: ${e.message || e}`;
      console.error("Unexpected error during PGN parsing:", e);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setPgnInput('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        padding: '20px',
        maxWidth: '90vw',
        width: '400px',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid #444',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
      }}>
        {/* Header */}
        <h2 style={{
          textAlign: 'center',
          color: 'gold',
          marginTop: 0,
          marginBottom: '20px',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          Upload PGN
        </h2>
        

        {/* Instructions */}
        <div style={{
          color: '#ccc',
          fontSize: '14px',
          marginBottom: '15px',
          lineHeight: '1.4',
          textAlign: 'center'
        }}>
          Paste or type your PGN notation below:
        </div>

        {/* PGN Input */}
        <textarea
          value={pgnInput}
          onChange={handlePGNInputChange}
          placeholder={"1. e4 e5 2. Nf3 ..." 
            + "                " + "Please use a RAW PGN, without annotations or variations. Support for those coming!"}
          disabled={isLoading}
          style={{
            width: '100%',
            height: '150px',
            padding: '12px',
            fontSize: '16px',
            backgroundColor: '#333',
            color: 'white',
            border: error ? '2px solid #ff6b6b' : '1px solid #666',
            borderRadius: '8px',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'monospace',
            boxSizing: 'border-box',
            marginBottom: '15px'
          }}
        />

        {/* Error Message */}
        {error && (
          <div style={{
            color: '#ff6b6b',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
            border: '1px solid #ff6b6b',
            borderRadius: '6px',
            padding: '10px',
            fontSize: '14px',
            marginBottom: '15px'
          }}>
            {error}
          </div>
        )}

        {/* Buttons */}
        <div style={{
          display: 'flex',
          gap: '10px',
          justifyContent: 'center'
        }}>
          <button
            onClick={handleCancel}
            disabled={isLoading}
            style={{
              padding: '12px 20px',
              backgroundColor: 'transparent',
              color: '#888',
              border: '1px solid #888',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !pgnInput.trim()}
            style={{
              padding: '12px 20px',
              backgroundColor: isLoading || !pgnInput.trim() ? '#555' : 'gold',
              color: isLoading || !pgnInput.trim() ? '#ccc' : 'black',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 'bold',
              cursor: (isLoading || !pgnInput.trim()) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {isLoading && (
              <i className="fa-solid fa-spinner fa-spin"></i>
            )}
            {isLoading ? 'Processing...' : 'Load PGN'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobilePGNUploadModal;